import React, { Component, ReactNode } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import { connect, ReactReduxContext } from 'react-redux';
import { RootState, routerHistory } from '../slices/rootReducer';
import LoggedInContainer from './auth/loggedInContainer';
import AppWrapper from '../components/common/layout/appWrapper';
import Notifications from '../components/common/notification';
import { AmplifyContainer } from './auth/amplifyContainer';
import { GOOGLE_TAG_MANAGER, NON_CORE_APP_URL } from '../config';
import IDPResponse from './auth/IDPResponse';
import Logout from './auth/Logout';
import { getRedirectUrlFromQuery } from '../utils/queryParameters';
import { selectors, setRedirectUrl } from '../slices/auth';
import { FeatureToggleClassAppender } from '../featureToggles/GlobalClassAppender';
import { AccountSettings } from './accounts/account/settings';
import { AccountBilling as AccountBillingForPLG3 } from './accounts/account/billingForPLG3';
import { AccountsDashboard as AccountsDashboardForPlgExternal } from './accounts/accountsDashboard';
import LoginScreen from './auth/login/login';
import { SignUpEmailLinkSent } from './auth/signUp/SignUpEmailLinkSent';
import { SignUpByEmail } from './auth/signUp/SignUpByEmail';
import { ApiKeySection } from './accounts/account/apiKey';
import { SettingsDashboard } from './settings/dashboard';
import { Profile } from './settings/profile';
import { CreditsUserFacing } from './settings/credits';
import { UsersSettingDashboard } from './settings/users';
import { Subscription } from './settings/subscription';
import { EditSubscription } from './settings/subscription/common/editSubscription';
import { AccessTypes } from './common/domain/accountAccessType';
import { BillingSettingsDashboard } from './settings/billing';
import { UserPilot } from './userpilot';
import { isOnlyRelease, isProductionEnv } from '../utils/platform';
import gtagScript from '../tagManager';
import { RecaptchaContainer } from './common/recaptchaContainer/RecaptchaContainer';
import { isPureProfileClient } from '../utils/clients';
import { CREDITS_PATH } from './common/navigationBar/accountsNavigationBar';
import { RouteIncluded } from '../featureToggles/forRoutes';
import { Features, isFeatureEnabled } from '../featureToggles/features';
import { ManageMinutes } from './video/ManageMinutes';
import { getDefaultCoreUrl } from './auth/utils/getUrlToRedirect';
import { TokenType } from '../slices/metadata';
import { getMetadata } from '../slices/metadata/selectors';
import { RepdataSSOLoginHandler } from './auth/repdata/RepdataSSOLoginHandler';
//  note recommended to use lazy by google recaptcha documentation

const ComparePlans = React.lazy(() => import('./settings/subscription/comparePlans'));
const UsersDashboard = React.lazy(() => import('./users'));
const AccountsDashboard = React.lazy(() => import('./accounts'));
const ResetPasswordRequest = React.lazy(() => import('./auth/resetPasswordRequest'));
const UpdatePassword = React.lazy(() => import('./auth/updatePassword'));
const CompleteRegistration = React.lazy(() => import('./auth/completeRegistration'));
const AcceptInvitation = React.lazy(() => import('./auth/acceptInvitation'));
const RegisteredToken = React.lazy(() => import('./auth/registeredToken'));
const TwoFactorAuth = React.lazy(() => import('./auth/twoFactorAuth'));
const RegistrationExpired = React.lazy(() => import('./auth/registrationExpired'));
const UpgradeInProgress = React.lazy(() => import('./upgradeInProgress'));
const ExpiredAccount = React.lazy(() => import('./expiredAccount'));
const CopyTemplate = React.lazy(() => import('./auth/copyTemplate'));
const InvoicesDashBoard = React.lazy(() => import('./invoices').then(module => ({ default: module.InvoicesDashBoard })));
const PlanAndBillingDashboard = React.lazy(() => import('./settings/planAndBilling').then(module => ({ default: module.PlanAndBillingDashboard })));
const SSODashboard = React.lazy(() => import('./sso'));
const QuestionPackagesDashboard = React.lazy(() => import('./questionPackages'));
const CreditPoolDashboard = React.lazy(() => import('./credits/views/CreditPoolDashboard').then(module => ({ default: module.CreditPoolDashboard })));
const CreditsPoolsDashboard = React.lazy(() => import('./credits/views/CreditsPoolsDashboard').then(module => ({ default: module.CreditsPoolsDashboard })));
const Video = React.lazy(() => import('./video').then(module => ({ default: module.Video })));

const ProUserRegistration = React.lazy(() => import('./auth/proRegister/nameAndPassword'));
const ProUserSubscription = React.lazy(() => import('./auth/proRegister/subscription'));
const ProUserPayment = React.lazy(() => import('./auth/proRegister/payment'));
const AccountCreation = React.lazy(() => import('./auth/proRegister/accountCreation'));

interface IRootRouterDispatchToProps {
  setRedirectUrl: typeof setRedirectUrl;
}

interface IMapStateToProps {
  coreUrl: string;
  isSharedDashboard: boolean;
  projectId?: string;
}

type Props = IRootRouterDispatchToProps & IMapStateToProps;

class RootRouter extends Component<Props> {
  componentDidMount(): void {
    this.renderTagManager();
    const { setRedirectUrl } = this.props;
    const redirectURL = getRedirectUrlFromQuery();
    if (redirectURL) {
      setRedirectUrl({ url: redirectURL });
    }
  }

  private renderTagManager(): void {
    const path = window.location.pathname;
    const shouldInjectGoogleTagManager = path.toLocaleLowerCase().startsWith('/signup');
    if ((isOnlyRelease() || isProductionEnv()) && shouldInjectGoogleTagManager) {
      const script = document.createElement('script');
      script.src = `${gtagScript(window, document, 'script', 'dataLayer', GOOGLE_TAG_MANAGER)}`;
      document.head.prepend(script);

      const noScript = document.createElement('noscript');
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.googletagmanager.com/ns.html?id=${GOOGLE_TAG_MANAGER}`;
      iframe.height = '0';
      iframe.width = '0';
      iframe.style.display = 'none';
      iframe.style.visibility = 'hidden';
      noScript.appendChild(iframe);
      document.body.prepend(noScript);
    }
  }

  render(): ReactNode {
    const { isSharedDashboard, coreUrl, projectId = '' } = this.props;
    if (isSharedDashboard) {
      const projectOverviewUrl = `${coreUrl}/analysis/${projectId}/overview`;
      window.location.replace(projectOverviewUrl);
      return null;
    }
    const redirectUrl = `${NON_CORE_APP_URL}${window.location.pathname}`;
    const defaultPath = '/accounts';
    return (
      <ConnectedRouter history={routerHistory} context={ReactReduxContext}>
        <FeatureToggleClassAppender />
        <Notifications />
        <UserPilot />
        <AmplifyContainer />
        <Switch>
          <Route path="/handle-sso" exact component={RepdataSSOLoginHandler} />
          <Route path="/login" exact component={() => <RecaptchaContainer><LoginScreen/></RecaptchaContainer>} />
          <Route path="/logout" exact component={Logout} />
          <Route path="/forgot-password" exact component={ResetPasswordRequest} />
          <Route path="/reset-password" exact component={UpdatePassword} />
          <Route path="/registered" exact component={RegisteredToken} />
          <Route path="/complete-registration" exact component={CompleteRegistration} />
          <Route path="/accept-invitation" exact component={AcceptInvitation} />
          <Route path="/registration-expired" exact component={RegistrationExpired} />
          <Route path="/twoFactorAuth" component={TwoFactorAuth} />
          <Route path="/signUp" exact component={() => <RecaptchaContainer><SignUpByEmail tier={AccessTypes.Lite} /></RecaptchaContainer>} />
          <Route path="/signUp/pro" exact component={() => <RecaptchaContainer><SignUpByEmail tier={AccessTypes.Professional} /></RecaptchaContainer>} />
          <Route path="/signUp/resend" exact component={() => <RecaptchaContainer><SignUpEmailLinkSent /></RecaptchaContainer>} />
          <Route path="/signUp/register/:token" exact component={() => <RecaptchaContainer><ProUserRegistration /></RecaptchaContainer>} />
          <LoggedInContainer
            redirectByPath={{
              '/copy-template/:templateId': `/signUp?redirectUrl=${redirectUrl}`,
              '/settings/billing': `/login?redirectUrl=${redirectUrl}`
            }}
          >
            <Switch>
              <Route path="/idpresponse" exact component={IDPResponse} />
              <Route path="/copy-template/:templateId" component={CopyTemplate} />
              <Route path="/expired-account" component={ExpiredAccount} />
              <Route path="/upgrade-in-progress" component={UpgradeInProgress} />
              <Route path="/signUp/pro/subscription" exact component={ProUserSubscription} />
              <Route path="/signUp/pro/payment/:clientSecret" exact component={ProUserPayment} />
              <Route path="/signUp/account-creation" exact component={AccountCreation} />
              <AppWrapper>
                <Route exact path="/">
                  <Redirect to={defaultPath} />
                </Route>
                <Route path="/accounts" exact component={AccountsDashboard} />
                <Route path="/accounts/:id/settings" exact>
                  <AccountsDashboardForPlgExternal>
                    <AccountSettings />
                  </AccountsDashboardForPlgExternal>
                </Route>
                <Route path="/accounts/:id/users" exact>
                  <AccountsDashboardForPlgExternal>
                    <UsersDashboard />
                  </AccountsDashboardForPlgExternal>
                </Route>
                <Route path="/accounts/:id/apiKey" exact>
                  <ApiKeySection />
                </Route>
                {!isPureProfileClient() &&
                <>
                  <Route
                    path="/accounts/:id/billing"
                    exact
                    render={(props) => {
                      const { id = '' } = props.match.params;
                      return <AccountBillingForPLG3 accountId={id} />;
                    }}
                  />
                  <Route path="/accounts/:id/invoices" exact>
                    <AccountsDashboardForPlgExternal>
                      <InvoicesDashBoard />
                    </AccountsDashboardForPlgExternal>
                  </Route>
                  <Route path="/accounts/:id/sso" exact>
                    <AccountsDashboardForPlgExternal>
                      <SSODashboard />
                    </AccountsDashboardForPlgExternal>
                  </Route>
                  <Route path="/accounts/:id/questionPackages" exact>
                    <QuestionPackagesDashboard />
                  </Route>
                  <Route path={`/accounts/:id/${CREDITS_PATH}`} exact>
                    <CreditsPoolsDashboard />
                  </Route>
                  <Route path={`/accounts/:accountId/${CREDITS_PATH}/:creditPoolId`} exact>
                    <CreditPoolDashboard />
                  </Route>
                  <RouteIncluded
                    inFeatures={[Features.VideoTracking]}
                    path="/accounts/:id/video"
                    exact
                  >
                    <AccountsDashboardForPlgExternal actions={[<ManageMinutes />]}>
                      <Video />
                    </AccountsDashboardForPlgExternal>
                  </RouteIncluded>
                </>
                }
                <Route path="/settings/profile" exact>
                  <SettingsDashboard><Profile /></SettingsDashboard>
                </Route>
                <Route path={['/settings/subscription', '/settings/subscription/upgrade-to-pro']} exact>
                  <SettingsDashboard><Subscription /></SettingsDashboard>
                </Route>
                <Route path="/settings/plan-billing" exact>
                  <SettingsDashboard>
                    <PlanAndBillingDashboard/>
                  </SettingsDashboard>
                </Route>
                <Route path="/settings/billing" exact>
                  <SettingsDashboard>
                    <BillingSettingsDashboard />
                  </SettingsDashboard>
                </Route>
                <Route path="/settings/users" exact>
                  <SettingsDashboard>
                    <UsersSettingDashboard />
                  </SettingsDashboard>
                </Route>
                <Route path="/settings/subscription/edit" exact>
                  <SettingsDashboard>
                    <EditSubscription />
                  </SettingsDashboard>
                </Route>
                <Route path="/settings/subscription/compare" exact>
                  <SettingsDashboard>
                    <ComparePlans />
                  </SettingsDashboard>
                </Route>
                <Route path="/settings/credits" exact>
                  <SettingsDashboard>
                    <CreditsUserFacing />
                  </SettingsDashboard>
                </Route>
                <Route path={`/settings/${CREDITS_PATH}/:accountId/:creditPoolId`} exact>
                  <CreditPoolDashboard />
                </Route>
              </AppWrapper>
            </Switch>
          </LoggedInContainer>
        </Switch>
      </ConnectedRouter>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  const user = selectors.getLoggedInUser(state);
  const coreUrl = getDefaultCoreUrl(user?.account);
  const metadata = getMetadata(state);
  const isSharedDashboard = isFeatureEnabled(Features.ShareableDashboards) && metadata?.tokenType === TokenType.SharedDashboard;
  const projectId = metadata?.projectId;
  return {
    coreUrl,
    isSharedDashboard,
    projectId
  };
};

export default connect(mapStateToProps, {
  setRedirectUrl
})(RootRouter);
